import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Paper,
  Snackbar,
  CircularProgress,
  Divider,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DescriptionIcon from "@mui/icons-material/Description";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ShareIcon from "@mui/icons-material/Share";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DrawIcon from "@mui/icons-material/Draw";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import PremiumPageLayout from "../../../templates/PremiumPageLayout";
import PremiumCard from "../../../molecules/PremiumCard";
import SectionHeader from "../../../molecules/SectionHeader";
import ContactDetailField from "../../../molecules/ContactDetailField";
import TextArea from "../../../atoms/Textarea";
import CarMediaUploaderViewer from "../../../molecules/CarMediaUploaderViewer";
import MySignatureCanvas from "../../../molecules/signature_canvas";
import ShareWithClientsModal from "../../../molecules/ShareWithClients";
import SuperAdminVerifationModal from "../../../molecules/SuperAdminVerifationModal";
import PrintComponent from "../../PrintComponent";
import PrebidFormDownload from "./save_as_pdf";
import { Bucket } from "../../../../api/config/apiConfig";
import { imageUpload } from "../../../../api/services/services";
import {
  getPrebidByUuid,
  postUpdateAdminPrebid,
  sendSuperAdminVerificationForEdit,
  sharePreviewLink,
} from "./service";
import moment from "moment";

const CreateUpdatePrebid = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const isNew = uuid === "uuid";

  const [activeTab, setActiveTab] = useState(0);
  const [isEdit, setIsEdit] = useState(isNew);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modals
  const [isSignCanvas, setSignCanvas] = useState(false);
  const [isShare, setIsShare] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  // Snackbar Notification
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const notify = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const [formContent, setFormContent] = useState({
    issueDate: moment().format("YYYY-MM-DD"),
    notice: "",
    contractPrice: "",
    date: moment().format("YYYY-MM-DD"),
    importantNote: "",
    instructions:
      "1. Passport Copy: A clear and legible copy of the passport of the authorized representative.\r2. Exemption Certificate: An official exemption certificate or import permit issued by the Ministry of Foreign Affairs.\r3. Copy of Diplomatic ID: A copy of the diplomatic identification card of the authorized representative.\r4. Authority Letter: o An authority letter issued by the Embassy/Organization, authorizing the collection of the consignment. o Authority Letter for Collecting and Clearing Consignment from Destination Port.\r5. Specimen Signature: A specimen signature of the authorized representative for verification purposes.\r6. Letter to Excise and Taxation Officer Karachi: A letter addressed to the Excise and Taxation Officer in Karachi, pertaining to the consignment.\r7. Undertaking for Transportation: A letter of undertaking, ensuring the transportation of the consignment is conducted as per the regulations and requirements.",
    inclusions:
      "• Purchase of Vehicle in Japan Auction\r• Export documentation, Taxes, Auction Fees and Handling in Japan\r• Sea Freight: Shipping costs from Yokohama/Nagoya, Japan to Karachi Port, Pakistan.\r• Destination Terminal Handling Charges (DTHC): Charges for handling the vehicle at the destination terminal in Karachi, Pakistan\r• Wharfage Charges\r• DO Charges: Delivery Order charges, necessary for the release of the cargo.\r• Transportation: Costs of transporting the vehicle from the port of Karachi to Islamabad.\r• Handling Charges during Local Transportation: Charges for handling the vehicle during its transportation from Karachi to Islamabad.",
    exclusions:
      "At the destination (Port of Karachi, Pakistan), the above-stated price excludes the following costs:",
    demurrage:
      "Demurrage refers to official storage charges imposed by the port authority for cargo that remains at the port beyond the allowed free time. Detention Charges: Fees for the extended use of containers beyond the agreed free period.",
    paymentTerms:
      "1. 50% Payment at the Time of Bill of Lading: price shall be paid at the submission of the Bill of Lading for the vehicle to the 1st Party (Buyer)\r2. 50% Payment at the Time of Delivery: The remaining 50% of the total contract price shall be paid upon the delivery of the vehicle to the 1st Party (Buyer) designated location in Islamabad.",
    carDescription: {
      make: "",
      model: "",
      year: "",
      grade: "",
      specification: "",
      mileage: "",
      images: [],
    },
    firstParty: {
      name: "",
      email: "",
      mofaIdNo: "",
      sign: "",
      organization: "",
    },
    secondParty: {
      name: "Shelozon Logistics",
      email: "info@shelozon.com",
      mofaIdNo: "",
      sign: "",
      organization: "Shelozon Movers Packers & Logistics Pvt Ltd",
    },
  });

  const getPrebidData = async () => {
    if (!isNew && uuid) {
      setIsLoading(true);
      getPrebidByUuid(uuid)
        .then((res) => {
          if (res?.data?.data) {
            setFormContent(res.data.data);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching prebid:", err);
          notify("Failed to fetch prebid agreement details", "error");
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    getPrebidData();
  }, [uuid]);

  const handleSavePrebid = () => {
    setIsSaving(true);
    postUpdateAdminPrebid(uuid, formContent)
      .then(() => {
        notify(
          isNew
            ? "Car Prebid agreement created successfully!"
            : "Car Prebid agreement updated successfully!",
          "success"
        );
        setIsSaving(false);
        setTimeout(() => {
          navigate("/prebid-agreement-list");
        }, 1000);
      })
      .catch((error) => {
        console.error("Error saving prebid:", error);
        notify(
          error?.response?.data?.message || "Failed to save prebid agreement",
          "error"
        );
        setIsSaving(false);
      });
  };

  const handleSharePreview = (emails) => {
    const validEmails = emails.filter((e) => e.trim().length > 0);
    if (validEmails.length === 0) return;

    setIsLoading(true);
    sharePreviewLink({ uuid, emails: validEmails })
      .then(() => {
        notify("Prebid agreement shared successfully with client(s)!");
        setIsShare(false);
        setIsLoading(false);
      })
      .catch((err) => {
        notify(err?.response?.data?.message || "Failed to share prebid", "error");
        setIsLoading(false);
      });
  };

  const handleRequestEdit = () => {
    setIsEditLoading(true);
    sendSuperAdminVerificationForEdit()
      .then(() => {
        setIsVerified(true);
        setIsEditLoading(false);
      })
      .catch(() => {
        setIsVerified(false);
        setIsEditLoading(false);
        notify("Failed to request edit verification code", "error");
      });
  };

  // Update second party signature
  const handleUploadSecondPartySignature = (file) => {
    const fd = new FormData();
    fd.append("image", file);
    imageUpload(fd)
      .then((res) => {
        const key = res?.data?.data?.key;
        if (key) {
          setFormContent((prev) => ({
            ...prev,
            secondParty: {
              ...prev.secondParty,
              sign: key,
            },
          }));
          notify("Signature uploaded successfully!", "success");
        }
        setSignCanvas(false);
      })
      .catch((err) => {
        notify(err?.response?.data?.message || "Failed to upload signature", "error");
        setSignCanvas(false);
      });
  };

  // Car images sync
  const handleCarImagesChange = (newImages) => {
    setFormContent((prev) => ({
      ...prev,
      carDescription: {
        ...prev.carDescription,
        images: newImages,
      },
    }));
  };

  return (
    <>
      <PremiumPageLayout
        title={
          isNew
            ? "Create Car Prebid Agreement"
            : `Car Prebid: ${formContent?.carDescription?.make || ""} ${formContent?.carDescription?.model || "Agreement"}`
        }
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {/* Edit Mode Button */}
            {!isEdit && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                disabled={isEditLoading}
                onClick={handleRequestEdit}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 700,
                  px: 2.5,
                  bgcolor: "#0284c7",
                  "&:hover": { bgcolor: "#0369a1" },
                }}
              >
                {isEditLoading ? "Requesting..." : "Edit Agreement"}
              </Button>
            )}

            {/* Share Button */}
            {!isEdit && !isNew && (
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => setIsShare(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 700,
                  px: 2.5,
                  borderColor: "#cbd5e1",
                  color: "#334155",
                  "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                }}
              >
                Share with Client
              </Button>
            )}

            {/* Cancel Button */}
            {isEdit && !isNew && (
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={() => setIsEdit(false)}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 700,
                  px: 2.5,
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                }}
              >
                Cancel
              </Button>
            )}

            {/* Save Button */}
            {isEdit && (
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
                disabled={isSaving}
                onClick={handleSavePrebid}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 700,
                  px: 3,
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  boxShadow: "0 4px 12px rgba(5, 150, 105, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #047857 0%, #065f46 100%)",
                  },
                }}
              >
                {isSaving ? "Saving..." : "Save Agreement"}
              </Button>
            )}

            {/* Save as PDF / Print */}
            {!isEdit && (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => {
                  if (window.innerWidth <= 768) {
                    notify("For a good PDF experience, please use a desktop screen", "info");
                    return;
                  }
                  const tempTitle = document.title;
                  document.title = `Prebid-Agreement-${formContent?.carDescription?.make || "Car"}-${moment().format("DD-MM-YYYY")}`;
                  window.print();
                  document.title = tempTitle;
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 700,
                  px: 2.5,
                  borderColor: "#0284c7",
                  color: "#0284c7",
                  "&:hover": { bgcolor: "#f0f9ff" },
                }}
              >
                Save as PDF
              </Button>
            )}
          </Box>
        }
      >
        {/* Main Tabs Header */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            p: 1,
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                borderRadius: "12px",
                minHeight: "44px",
                px: 2.5,
                mr: 1,
                color: "#64748b",
                "&.Mui-selected": {
                  color: "#0284c7",
                  backgroundColor: "#e0f2fe",
                },
              },
            }}
          >
            <Tab
              icon={<DirectionsCarIcon />}
              iconPosition="start"
              label="1. Vehicle Profile"
            />
            <Tab
              icon={<DescriptionIcon />}
              iconPosition="start"
              label="2. Terms & Clauses"
            />
            <Tab
              icon={<HowToRegIcon />}
              iconPosition="start"
              label="3. Parties & Signatures"
            />
            <Tab
              icon={<PhotoLibraryIcon />}
              iconPosition="start"
              label={`4. Car Photos & Media (${formContent?.carDescription?.images?.length || 0})`}
            />
          </Tabs>
        </Paper>

        {/* TAB 1: VEHICLE PROFILE & SPECS */}
        {activeTab === 0 && (
          <PremiumCard>
            <SectionHeader
              icon={<DirectionsCarIcon />}
              title="Car Specifications & Profile"
              subtitle="Enter the vehicle's make, model, manufacturing year, specification, and mileage details."
            />

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Make / Manufacturer
                </Typography>
                <ContactDetailField
                  label="Make"
                  isEdit={isEdit}
                  type="text"
                  value={formContent?.carDescription?.make}
                  placeholder="e.g. Toyota, Honda, Mercedes-Benz"
                  onChange={(e) => {
                    setFormContent({
                      ...formContent,
                      carDescription: { ...formContent.carDescription, make: e.target.value },
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Model & Variant
                </Typography>
                <ContactDetailField
                  label="Model"
                  isEdit={isEdit}
                  type="text"
                  value={formContent?.carDescription?.model}
                  placeholder="e.g. Land Cruiser Prado TX, Civic"
                  onChange={(e) => {
                    setFormContent({
                      ...formContent,
                      carDescription: { ...formContent.carDescription, model: e.target.value },
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Model Year
                </Typography>
                <ContactDetailField
                  label="Year"
                  isEdit={isEdit}
                  type="text"
                  value={formContent?.carDescription?.year}
                  placeholder="e.g. 2022, 2023"
                  onChange={(e) => {
                    setFormContent({
                      ...formContent,
                      carDescription: { ...formContent.carDescription, year: e.target.value },
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Grade / Package
                </Typography>
                <ContactDetailField
                  label="Grade"
                  isEdit={isEdit}
                  type="text"
                  value={formContent?.carDescription?.grade}
                  placeholder="e.g. Package L, Grade 4.5"
                  onChange={(e) => {
                    setFormContent({
                      ...formContent,
                      carDescription: { ...formContent.carDescription, grade: e.target.value },
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Specification & Options
                </Typography>
                <ContactDetailField
                  label="Specification"
                  isEdit={isEdit}
                  type="text"
                  value={formContent?.carDescription?.specification}
                  placeholder="e.g. 4WD, Sunroof, Leather"
                  onChange={(e) => {
                    setFormContent({
                      ...formContent,
                      carDescription: { ...formContent.carDescription, specification: e.target.value },
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Mileage
                </Typography>
                <ContactDetailField
                  label="Mileage"
                  isEdit={isEdit}
                  type="text"
                  value={formContent?.carDescription?.mileage}
                  placeholder="e.g. 25,000 km"
                  onChange={(e) => {
                    setFormContent({
                      ...formContent,
                      carDescription: { ...formContent.carDescription, mileage: e.target.value },
                    });
                  }}
                />
              </Grid>
            </Grid>

            {/* Quick Media Preview inside tab 1 */}
            <Divider sx={{ my: 4 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
                  Attached Car Photos & Inspection Sheets
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  {formContent?.carDescription?.images?.length || 0} photo(s) currently uploaded.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<PhotoLibraryIcon />}
                onClick={() => setActiveTab(3)}
                sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
              >
                Open Full Media Uploader & Viewer
              </Button>
            </Box>
          </PremiumCard>
        )}

        {/* TAB 2: TERMS, FINANCIALS & CLAUSES */}
        {activeTab === 1 && (
          <PremiumCard>
            <SectionHeader
              icon={<DescriptionIcon />}
              title="Agreement Clauses & Financial Terms"
              subtitle="Define contract pricing, dates, confidentiality notices, inclusions, exclusions, and payment terms."
            />

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Issue Date
                </Typography>
                <ContactDetailField
                  label="Issue Date"
                  isEdit={isEdit}
                  type="Date"
                  value={formContent?.issueDate}
                  onChange={(e) => setFormContent({ ...formContent, issueDate: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Agreement Date
                </Typography>
                <ContactDetailField
                  label="Agreement Date"
                  isEdit={isEdit}
                  type="Date"
                  value={formContent?.date}
                  onChange={(e) => setFormContent({ ...formContent, date: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                  Contract Price
                </Typography>
                <ContactDetailField
                  label="Contract Price"
                  isEdit={isEdit}
                  type="text"
                  value={formContent?.contractPrice}
                  placeholder="e.g. $45,000 USD / ¥6,500,000 JPY"
                  onChange={(e) => setFormContent({ ...formContent, contractPrice: e.target.value })}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Long Text Areas */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                  Confidentiality Notice
                </Typography>
                {!isEdit ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155" }}>
                    {formContent?.notice || "No confidentiality notice specified."}
                  </Paper>
                ) : (
                  <TextArea
                    rows="3"
                    label="Confidentiality Notice..."
                    value={formContent?.notice}
                    onChange={(e) => setFormContent({ ...formContent, notice: e.target.value })}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                  Important Note
                </Typography>
                {!isEdit ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155" }}>
                    {formContent?.importantNote || "No important note specified."}
                  </Paper>
                ) : (
                  <TextArea
                    rows="3"
                    label="Important Note..."
                    value={formContent?.importantNote}
                    onChange={(e) => setFormContent({ ...formContent, importantNote: e.target.value })}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                  Instructions & Required Documentation
                </Typography>
                {!isEdit ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155", whiteSpace: "pre-line" }}>
                    {formContent?.instructions || "No instructions specified."}
                  </Paper>
                ) : (
                  <TextArea
                    rows="4"
                    label="Instructions..."
                    value={formContent?.instructions}
                    onChange={(e) => setFormContent({ ...formContent, instructions: e.target.value })}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                  Inclusions
                </Typography>
                {!isEdit ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155", whiteSpace: "pre-line" }}>
                    {formContent?.inclusions || "No inclusions specified."}
                  </Paper>
                ) : (
                  <TextArea
                    rows="4"
                    label="Inclusions..."
                    value={formContent?.inclusions}
                    onChange={(e) => setFormContent({ ...formContent, inclusions: e.target.value })}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                  Exclusions
                </Typography>
                {!isEdit ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155", whiteSpace: "pre-line" }}>
                    {formContent?.exclusions || "No exclusions specified."}
                  </Paper>
                ) : (
                  <TextArea
                    rows="4"
                    label="Exclusions..."
                    value={formContent?.exclusions}
                    onChange={(e) => setFormContent({ ...formContent, exclusions: e.target.value })}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                  Demurrage & Detention Terms
                </Typography>
                {!isEdit ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155" }}>
                    {formContent?.demurrage || "No demurrage terms specified."}
                  </Paper>
                ) : (
                  <TextArea
                    rows="3"
                    label="Demurrage..."
                    value={formContent?.demurrage}
                    onChange={(e) => setFormContent({ ...formContent, demurrage: e.target.value })}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
                  Payment Terms
                </Typography>
                {!isEdit ? (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155", whiteSpace: "pre-line" }}>
                    {formContent?.paymentTerms || "No payment terms specified."}
                  </Paper>
                ) : (
                  <TextArea
                    rows="3"
                    label="Payment Terms..."
                    value={formContent?.paymentTerms}
                    onChange={(e) => setFormContent({ ...formContent, paymentTerms: e.target.value })}
                  />
                )}
              </Grid>
            </Grid>
          </PremiumCard>
        )}

        {/* TAB 3: PARTIES & SIGNATURES */}
        {activeTab === 2 && (
          <PremiumCard>
            <SectionHeader
              icon={<HowToRegIcon />}
              title="Parties & Digital Signatures"
              subtitle="Authorized representative details and digital signature verification for both parties."
            />

            <Grid container spacing={4} sx={{ mt: 1 }}>
              {/* 1st Party: Buyer */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "14px",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 2 }}>
                    1st Party (Buyer)
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      Representative Name
                    </Typography>
                    <ContactDetailField
                      label="Name"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.firstParty?.name}
                      placeholder="Buyer full name"
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          firstParty: { ...formContent.firstParty, name: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      Email Address
                    </Typography>
                    <ContactDetailField
                      label="Email"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.firstParty?.email}
                      placeholder="buyer@example.com"
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          firstParty: { ...formContent.firstParty, email: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      MOFA ID / Diplomatic ID No
                    </Typography>
                    <ContactDetailField
                      label="MOFA ID"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.firstParty?.mofaIdNo}
                      placeholder="e.g. MOFA-PK-12345"
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          firstParty: { ...formContent.firstParty, mofaIdNo: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      Organization / Embassy
                    </Typography>
                    <ContactDetailField
                      label="Organization"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.firstParty?.organization}
                      placeholder="Embassy or company name"
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          firstParty: { ...formContent.firstParty, organization: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Buyer Signature Box */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
                    Buyer Signature (Captured during Client Acceptance)
                  </Typography>

                  <Box
                    sx={{
                      height: 100,
                      backgroundColor: "#ffffff",
                      borderRadius: "10px",
                      border: "1px dashed #cbd5e1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                    }}
                  >
                    {formContent?.firstParty?.sign ? (
                      <img
                        src={`${Bucket}${formContent.firstParty.sign}`}
                        alt="Buyer Signature"
                        style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                        Pending client preview digital acceptance
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* 2nd Party: Shelozon Logistics */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "14px",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 2 }}>
                    2nd Party (Shelozon Logistics)
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      Authorized Signatory Name
                    </Typography>
                    <ContactDetailField
                      label="Name"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.secondParty?.name}
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          secondParty: { ...formContent.secondParty, name: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      Email Address
                    </Typography>
                    <ContactDetailField
                      label="Email"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.secondParty?.email}
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          secondParty: { ...formContent.secondParty, email: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      Registration / MOFA ID
                    </Typography>
                    <ContactDetailField
                      label="MOFA ID"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.secondParty?.mofaIdNo}
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          secondParty: { ...formContent.secondParty, mofaIdNo: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                      Company Name
                    </Typography>
                    <ContactDetailField
                      label="Organization"
                      isEdit={isEdit}
                      type="text"
                      value={formContent?.secondParty?.organization}
                      onChange={(e) =>
                        setFormContent({
                          ...formContent,
                          secondParty: { ...formContent.secondParty, organization: e.target.value },
                        })
                      }
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Shelozon Signature Box */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
                    Shelozon Official Signature
                  </Typography>

                  <Box
                    sx={{
                      height: 100,
                      backgroundColor: "#ffffff",
                      borderRadius: "10px",
                      border: "1px dashed #cbd5e1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      p: 1,
                    }}
                  >
                    {formContent?.secondParty?.sign ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <img
                          src={`${Bucket}${formContent.secondParty.sign}`}
                          alt="Shelozon Signature"
                          style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain" }}
                        />
                        {isEdit && (
                          <Tooltip title="Remove Signature">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setFormContent({
                                  ...formContent,
                                  secondParty: { ...formContent.secondParty, sign: "" },
                                });
                              }}
                              sx={{ color: "#dc2626" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ) : isEdit ? (
                      <Button
                        variant="outlined"
                        startIcon={<DrawIcon />}
                        onClick={() => setSignCanvas(true)}
                        sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
                      >
                        Draw / Upload Signature
                      </Button>
                    ) : (
                      <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                        Unsigned
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </PremiumCard>
        )}

        {/* TAB 4: CAR PHOTOS & MEDIA UPLOADER & VIEWER */}
        {activeTab === 3 && (
          <PremiumCard>
            <CarMediaUploaderViewer
              images={formContent?.carDescription?.images || []}
              onImagesChange={handleCarImagesChange}
              isEdit={isEdit}
              onNotify={notify}
              title={`Vehicle Media: ${formContent?.carDescription?.make || "Car"} ${formContent?.carDescription?.model || ""}`}
              subtitle="Upload PNG and JPG high-res car exterior, interior, engine bay photos, auction sheets, & condition marks."
            />
          </PremiumCard>
        )}

        {/* Signature Canvas Modal */}
        {isSignCanvas && (
          <MySignatureCanvas
            open={isSignCanvas}
            onClose={() => setSignCanvas(false)}
            onSuccess={handleUploadSecondPartySignature}
          />
        )}

        {/* Share Preview Modal */}
        {isShare && (
          <ShareWithClientsModal
            open={isShare}
            onClose={() => setIsShare(false)}
            onSuccess={handleSharePreview}
          />
        )}

        {/* SuperAdmin OTP Edit Verification Modal */}
        {isVerified && (
          <SuperAdminVerifationModal
            open={isVerified}
            onClose={() => setIsVerified(false)}
            onSuccess={() => {
              setIsEdit(true);
              setIsVerified(false);
              notify("Admin edit verification verified successfully!", "success");
            }}
            onError={() => {
              notify("Invalid verification PIN entered", "error");
            }}
          />
        )}

        {/* Global Notifications Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={() => setOpenSnackbar(false)}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </PremiumPageLayout>

      {/* Printable Component Container (outside no-print layout) */}
      <PrintComponent>
        <PrebidFormDownload formContent={formContent} />
      </PrintComponent>
    </>
  );
};

export default CreateUpdatePrebid;
